import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';
import { CustomerRestDataProvider } from '../../../data/CustomerRestDataProvider';
import { Customer } from '../../../models/Customer';
import CustomerDialog from './CustomerDialog';
import './Customers.scss';
import '../../../styles/GenericSiteStyles.scss';
import { useTranslation } from 'react-i18next';

const Customers: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const provider = useMemo(() => new CustomerRestDataProvider(), []);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCustomers(await provider.getAll());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const columns: DetailedListColumn<Customer>[] = useMemo(() => [
    {
      key: 'name',
      header: t('screens:customers.columns.name'),
      dataField: 'name',
      type: 'string',
    },
    {
      key: 'isCompany',
      header: t('common:type'),
      dataField: 'isCompany',
      width: 110,
      fixedWidth: true,
      render: c => c.isCompany
        ? <span className="badge bg-primary">{t('screens:customers.badges.company')}</span>
        : <span className="badge bg-secondary">{t('screens:customers.badges.individual')}</span>,
    },
    {
      key: 'emailAddress',
      header: t('screens:customers.columns.email'),
      dataField: 'emailAddress',
      type: 'string',
    },
    {
      key: 'phoneNumber',
      header: t('screens:customers.columns.phone'),
      dataField: 'phoneNumber',
      type: 'string',
      width: 150,
      fixedWidth: true,
    },
    {
      key: 'country',
      header: t('screens:customers.columns.country'),
      dataField: 'country',
      type: 'string',
      width: 130,
      fixedWidth: true,
    },
  ], [t]);

  const openEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDialogOpen(true);
  };

  const openAdd = () => {
    setSelectedCustomer(null);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleSave = async (customer: Customer): Promise<Customer> => {
    try {
      if (!customer.id) {
        const saved = { ...customer, id: crypto.randomUUID() };
        await provider.add(saved);
        await loadCustomers();
        return saved;
      } else {
        await provider.update(customer.id, customer);
        await loadCustomers();
        return customer;
      }
    } catch (e: any) {
      setError(e.message);
      throw e;
    }
  };

  const handleDelete = async (customer: Customer) => {
    try {
      await provider.delete(customer.id);
      closeDialog();
      await loadCustomers();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <RoleGuard roles={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`} redirectOnDeny>
      <div className="customers-page">
        <MainScreenHeader title={t('screens:customers.title')} />
        {error && <ErrorMessage message={error} />}
        <DetailedListComponent
          dataSource={{ data: customers, setData: setCustomers }}
          columns={columns}
          rowKey={c => c.id}
          editRole={`${UserRoles.Admin},${UserRoles.Manager}`}
          addRole={`${UserRoles.Admin},${UserRoles.Manager}`}
          onEditRequest={openEdit}
          onAddRequest={openAdd}
        />
        {loading && <div className="customers-loading">{t('common:loading')}</div>}
        <CustomerDialog
          open={dialogOpen}
          customer={selectedCustomer}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={closeDialog}
        />
      </div>
    </RoleGuard>
  );
};

export default Customers;
